/**
 * useScrollSpy — suit la section visible parmi une liste d'ancres (#id) et
 * renvoie l'id actif. Fonctionne aussi bien sur `window` (Centre d'aide pleine
 * page) que sur un conteneur scrollable interne (viewport du Help Drawer).
 *
 * Généralise la logique de scroll-spy historiquement inline dans
 * KnowledgeArticle.tsx.
 */

import { useEffect, useState } from 'react'
import type { TocItem } from '@/lib/toc'

interface UseScrollSpyOptions {
    /**
     * Renvoie le conteneur scrollable. `null`/absent => `window`.
     * À mémoriser côté appelant (`useCallback`) pour éviter de relancer l'effet
     * à chaque rendu.
     */
    getContainer?: () => HTMLElement | null
    /** Décalage haut (px) avant qu'une section soit considérée active. */
    offset?: number
}

export function useScrollSpy(items: TocItem[], options: UseScrollSpyOptions = {}): string {
    const { getContainer, offset = 120 } = options
    const [activeSection, setActiveSection] = useState('')

    useEffect(() => {
        if (items.length === 0) {
            setActiveSection('')
            return
        }

        const container = getContainer?.() ?? null
        const scrollTarget: HTMLElement | Window = container ?? window

        const compute = () => {
            const containerTop = container ? container.getBoundingClientRect().top : 0
            let activeId = items[0]?.id ?? ''
            for (const item of items) {
                const el = document.getElementById(item.id)
                if (!el) continue
                const top = el.getBoundingClientRect().top - containerTop
                if (top <= offset) activeId = item.id
                else break
            }
            setActiveSection(activeId)
        }

        const timeoutId = window.setTimeout(compute, 100)
        scrollTarget.addEventListener('scroll', compute, { passive: true })

        return () => {
            window.clearTimeout(timeoutId)
            scrollTarget.removeEventListener('scroll', compute)
        }
    }, [items, getContainer, offset])

    return activeSection
}
